<template>
  <div class="oai-InviteStep_Wrapper">
    <div class="oai-InviteStep_Form">
      <div class="oai-InviteStep_EmailCollectorInputWrapper">
        <EmailCollectorInput
          ref="emailCollectorInputReference"
          :model-value="users"
          :placeholder="$t('onboarding.ai.invite.placeholder')"
          class="oai-InviteStep_EmailCollectorInput"
          icon="user-round"
          size="6xlg"
          @update:model-value="updateEmailsList"
        />

        <div class="oai-InviteStep_EmailLabel">
          {{ $t('organization.invite_users.email_label') }}
        </div>
      </div>

      <div v-if="users.length" class="oai-InviteStep_Chips oboard-truncated-text">
        <AppSelectedItemsList
          :selected-items="preparedUsers"
          class="oai-InviteStep_Chip"
          @delete="removeEmail"
        />
      </div>
    </div>
    <div class="oai-InviteStep_Actions">
      <!-- 
        NOTE:
        The @mousedown.prevent handler here is crucial to solve an issue where,
        if we enter the first email in EmailCollectorInput and immediately click the button below
        (to extract the valid email from the input), it keeps the focus on the input field.
        Without this, a layout shift can occur and the button click is not properly registered,
        resulting in the click event handler not firing and the user unable to proceed.
      -->
      <AppButtonNext
        :disabled="isSubmitting || isNavigating"
        class="oai-InviteStep_SubmitAction"
        icon-after="arrow-right-simple-next"
        @mousedown.prevent
        @click="handleSubmit"
      >
        {{ $t('action.continue') }}
      </AppButtonNext>
    </div>
  </div>
</template>

<script setup>
import { cloneDeep, has, isEmpty, uniqBy } from 'lodash'
import { computed, ref, useTemplateRef } from 'vue'

import WebAppOrganizationApiHandler from '@/api/web-app-organization'
import { useOnboardingAiStepContext } from '@/composables/onboarding-ai-step'
import { trackInviteUsersEvent } from '@/tracking/gtm-helpers'
import { ONBOARDING_ADMIN_ROLE } from '@/utils/admin-roles'
import { handleError } from '@/utils/error-handling'
import EmailCollectorInput from '@shared-modules/components/EmailCollectorInput'

import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppSelectedItemsList from '@/components/ui/AppSelect/AppSelectedItemsList'

const emit = defineEmits({
  next: null
})

const DEFAULT_INVITE_ROLE = ONBOARDING_ADMIN_ROLE

const users = ref([])

const emailCollectorInputReference = useTemplateRef('emailCollectorInputReference')

const stepContext = useOnboardingAiStepContext()
const isNavigating = computed(() => Boolean(stepContext?.navigating))

const isSubmitting = ref(false)

const preparedUsers = computed(() => {
  return users.value.map(item => {
    return {
      ...item,
      label: item.email,
      value: item.email
    }
  })
})

const mergeWithModelValue = validEmails => {
  const newItems = validEmails.map(email => ({
    ...email,
    roleId: DEFAULT_INVITE_ROLE
  }))
  users.value = uniqBy([...cloneDeep(users.value), ...newItems], 'email').map(user => ({
    email: user.email,
    roleId: has(user, 'roleId') ? user.roleId : DEFAULT_INVITE_ROLE
  }))
}

const updateEmailsList = validEmails => {
  if (!isEmpty(validEmails)) {
    mergeWithModelValue(validEmails)
  }
}

const removeEmail = email => {
  users.value = users.value.filter(item => item.email !== email)
}

const sendInviteToUsers = async () => {
  const api = new WebAppOrganizationApiHandler()

  await api.inviteUsersToCompany({ users: users.value })
  trackInviteUsersEvent()
}

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return
  }

  const _users = emailCollectorInputReference.value.resolveFinalEmailsList()
  mergeWithModelValue(_users)

  if (!isEmpty(users.value)) {
    try {
      isSubmitting.value = true
      await sendInviteToUsers()
    } catch (error) {
      handleError({ error })
      return
    } finally {
      isSubmitting.value = false
    }
  }

  emit('next', {
    invitees_count: users.value.length
  })
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';
@import '~@/assets/styles/wizard-steps';

.oai-InviteStep_Wrapper {
  @extend %oai-wizard-step-shell;
}

.oai-InviteStep_Form {
  display: grid;
  gap: 16px;
  width: 100%;
}

.oai-InviteStep_EmailCollectorInputWrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.oai-InviteStep_Chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}

.oai-InviteStep_Chip {
  width: 100%;

  &:deep(.si-SelectedItem) {
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .si-selected-tag-clear {
      flex-shrink: 0;
    }
  }

  &:deep(.si-SelectedTagClear) {
    color: $primary-color-next;

    &:hover {
      color: $white;
    }
  }
}

.oai-InviteStep_Actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 32px;
  width: 100%;
}

.oai-InviteStep_SubmitAction {
  min-height: 40px;
  padding: 6px 12px;
}

.oai-InviteStep_EmailCollectorInput {
  --ob-ai-padding-primary: 16px;
  --ob-ai-border-radius-primary: #{$border-radius-md};
  --ob-ai-border-color-primary: #{$primary-color-next};
  --prepend-padding-left: 0;
  --input-with-prepend-padding-left: 16px;
}

.oai-InviteStep_EmailLabel {
  font-size: $fs-12;
  line-height: 16px;
  font-weight: fw('regular');
  color: $oai-text-muted;
}
</style>
